import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useState } from "react";
import { EditBase, Form, useGetList, useRecordContext } from "react-admin";
import { Instructor } from "../providers/instructorsProvider";
import { Student } from "../providers/studentsProvider";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { Controller, FieldValues } from "react-hook-form";
import { assignInstructors } from "../api/backoffice.api";
import { SaveButton2 } from "../buttons/SaveButton2";

export function AssignInstructorDialog(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;
  const student = useRecordContext<Student>();
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const { data: instructors = [], isLoading } = useGetList<Instructor>("instructors", {
    filter: { drivingSchoolId: student.drivingSchoolId },
    pagination: { page: 1, perPage: 1000 },
  });

  const normalizedSearch = search.trim().toLocaleLowerCase("de");
  const renderedInstructors = instructors
    .filter(
      (instructor) =>
        (instructor.isActive || student.instructorIds.includes(instructor.id)) &&
        instructor.name.toLocaleLowerCase("de").includes(normalizedSearch),
    )
    .sort((first, second) => first.name.localeCompare(second.name));
  const instructorGroups = [
    {
      id: "sameBranch",
      label: "Gleiche Filiale",
      emptyText: "Keine Fahrlehrer dieser Filiale zugeordnet.",
      instructors: renderedInstructors.filter(
        (instructor) => student.branchId && instructor.assignedBranches.includes(student.branchId),
      ),
    },
    {
      id: "otherBranches",
      label: student.branchId ? "Weitere Fahrlehrer" : "Fahrlehrer",
      emptyText: student.branchId ? "Keine weiteren Fahrlehrer vorhanden." : "Keine Fahrlehrer vorhanden.",
      instructors: renderedInstructors.filter(
        (instructor) => !student.branchId || !instructor.assignedBranches.includes(student.branchId),
      ),
    },
  ];

  const onSubmit = async (data: FieldValues) => {
    if (!student) {
      throw new Error("Invalid state: !student");
    }
    const { instructorIds } = data as { instructorIds: Array<string> };
    setSaving(true);
    await runSafely(() => assignInstructors(student, instructorIds), {
      errorMessage:
        instructorIds.length === 1 ? `Fehler beim Ändern des Fahrlehrers.` : `Fehler beim Ändern der Fahrlehrer.`,
      successMessage: `Fahrlehrer erfolgreich geändert.`,
    });
    setSaving(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          height: 720,
          maxHeight: "calc(100dvh - 64px)",
          overflow: "hidden",
          "& > form": {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          },
        },
      }}
    >
      <DialogTitle>Fahrlehrer von {student.name} ändern</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <EditBase>
        <Form onSubmit={onSubmit} warnWhenUnsavedChanges>
          <DialogContent sx={{ flex: "0 0 auto", overflow: "visible", pb: 1 }}>
            <TextField
              label="Fahrlehrer suchen"
              type="search"
              fullWidth
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.preventDefault();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogContent sx={{ minHeight: 0 }}>
            <Controller<{ instructorIds: string[] }, "instructorIds">
              name="instructorIds"
              defaultValue={student.instructorIds}
              render={({ field }) => (
                <>
                  {instructorGroups
                    .filter((group) => group.id !== "sameBranch" || student.branchId)
                    .map((group) => (
                      <FormControl key={group.id} component="fieldset" margin="dense" fullWidth>
                        <FormLabel component="legend">{group.label}</FormLabel>
                        {!isLoading && !normalizedSearch && group.instructors.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            {group.emptyText}
                          </Typography>
                        )}
                        <FormGroup>
                          {group.instructors.map((instructor) => (
                            <FormControlLabel
                              key={instructor.id}
                              label={instructor.name}
                              control={
                                <Checkbox
                                  name={field.name}
                                  value={instructor.id}
                                  checked={field.value.includes(instructor.id)}
                                  disabled={saving}
                                  onBlur={field.onBlur}
                                  onChange={(event) =>
                                    field.onChange(
                                      event.target.checked
                                        ? [...field.value, instructor.id]
                                        : field.value.filter((id) => id !== instructor.id),
                                    )
                                  }
                                  inputRef={instructor.id === renderedInstructors[0]?.id ? field.ref : undefined}
                                />
                              }
                            />
                          ))}
                        </FormGroup>
                      </FormControl>
                    ))}
                </>
              )}
            />
            {!isLoading && normalizedSearch && renderedInstructors.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Keine passenden Fahrlehrer gefunden.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ pt: 2, borderTop: 1, borderColor: "divider" }}>
            <SaveButton2 />
          </DialogActions>
        </Form>
      </EditBase>
    </Dialog>
  );
}
